// ============================================================
// Asset loading — CRA/webpack compatible (no import.meta.glob)
// ============================================================
const ctx = require.context(
  '../../assets/games/pix',
  false,
  /\.(png|jpg|jpeg|webp)$/i
)

const modules = Object.fromEntries(
  ctx.keys().map((key) => [
    key.replace(/^\.\//, '../../assets/games/pix/'),
    ctx(key),
  ])
)

// ============================================================
// Helpers
// ============================================================
function slugify(name) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

function prettyLabel(file) {
  return file
    .replace(/^Copy of\s+/i, '')
    .replace(/\s+\d+$/, '')
    .trim()
}

// ============================================================
// PUZZLE_ASSETS — same shape as before
// ============================================================
export const PUZZLE_ASSETS = Object.entries(modules)
  .map(([path, src]) => {
    const file = path.split('/').pop().replace(/\.[^.]+$/, '')
    return {
      id: slugify(file),
      src,
      label: prettyLabel(file),
      kind: 'photo',
    }
  })
  .sort((a, b) => a.label.localeCompare(b.label))

// ============================================================
// Caches
// ============================================================
const imageCache = new Map()
const renderCache = new Map()

// ============================================================
// Internal helpers
// ============================================================
function waitForImage(img) {
  if (img.complete && img.naturalWidth > 0) {
    return img.decode?.().catch(() => undefined).then(() => img) ?? Promise.resolve(img)
  }
  return new Promise((resolve, reject) => {
    img.onload = () => {
      const done = () => resolve(img)
      if (img.decode) img.decode().then(done).catch(done)
      else done()
    }
    img.onerror = () => reject(new Error('image load failed'))
  })
}

// ============================================================
// Public API — identical signatures to the Vite version
// ============================================================
export function getPuzzleAsset(id) {
  return PUZZLE_ASSETS.find((a) => a.id === id) || PUZZLE_ASSETS[0]
}

export function randomPuzzleId(exclude = []) {
  const pool = PUZZLE_ASSETS.filter((a) => !exclude.includes(a.id))
  const list = pool.length ? pool : PUZZLE_ASSETS
  return list[Math.floor(Math.random() * list.length)].id
}

export async function loadPuzzleImage(assetId) {
  const cached = imageCache.get(assetId)
  if (cached) return cached

  const asset = getPuzzleAsset(assetId)
  if (!asset) return null

  const img = new Image()
  img.decoding = 'async'
  img.src = asset.src
  try {
    await waitForImage(img)
    if (img.naturalWidth > 0) {
      imageCache.set(assetId, img)
      return img
    }
  } catch {
    return null
  }
  return null
}

export async function renderPuzzleImage(assetId, size = 720) {
  const key = `${assetId}::${size}`
  if (renderCache.has(key)) return renderCache.get(key)

  const img = await loadPuzzleImage(assetId)
  if (!img) return null

  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx2d = canvas.getContext('2d')
  if (!ctx2d) return null

  ctx2d.fillStyle = '#001E3A'
  ctx2d.fillRect(0, 0, size, size)

  const aspect = img.naturalWidth / img.naturalHeight || 1
  let dw, dh, dx, dy
  if (aspect >= 1) {
    dh = size
    dw = size * aspect
    dx = (size - dw) / 2
    dy = 0
  } else {
    dw = size
    dh = size / aspect
    dx = 0
    dy = (size - dh) / 2
  }
  ctx2d.drawImage(img, dx, dy, dw, dh)

  const url = canvas.toDataURL('image/jpeg', 0.9)
  renderCache.set(key, url)
  return url
}